import { useState, useEffect } from "react";
import axios from "axios";
import { Dna, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  1: { label: "STESSA LEGA · 24 mesi", color: "#22FFA3" },
  2: { label: "STESSO PAESE · fallback", color: "#9DF6FF" },
  3: { label: "GLOBALE · fallback", color: "#F6C643" },
  0: { label: "DATI INSUFFICIENTI", color: "var(--bc-sub, #71717A)" },
};

const CONFIDENCE = (score) => {
  if (score >= 70) return { label: "FORTE", color: "#22FFA3", glow: "0 0 12px rgba(34,255,163,0.45)" };
  if (score >= 50) return { label: "MEDIO", color: "#F6C643", glow: "0 0 12px rgba(246,198,67,0.45)" };
  return { label: "DEBOLE", color: "#B487FF", glow: "0 0 12px rgba(180,135,255,0.40)" };
};

const CATEGORY_ACCENT = {
  "1X2": "#9DF6FF",
  "GOL": "#22FFA3",
  "OU": "#F6C643",
  "MULTIGOL": "#FF6FD8",
  "MULTIGOL_CASA": "#FF6FD8",
  "MULTIGOL_TRASFERTA": "#FF6FD8",
};
const CATEGORY_LABELS = {
  "1X2": "1X2",
  "GOL": "GOL/NO GOL",
  "OU": "OVER/UNDER",
  "MULTIGOL": "MULTIGOL TOT",
  "MULTIGOL_CASA": "MULTIGOL CASA",
  "MULTIGOL_TRASFERTA": "MULTIGOL TRASF.",
};

export function MatchDNAPanel({ fixtureId, homeTeamName, awayTeamName }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dna, setDna] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || dna || loading) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/match/${fixtureId}/dna`)
      .then((r) => setDna(r.data?.dna || null))
      .catch((e) => setError(e?.response?.data?.detail || "Errore nel calcolo del Match DNA"))
      .finally(() => setLoading(false));
  }, [open, fixtureId, dna, loading]);

  const tier = dna ? TIER_LABELS[dna.filter_tier_used ?? 0] || TIER_LABELS[0] : null;
  const conf = dna ? CONFIDENCE(dna.strength_score || 0) : null;
  const topPicks = dna?.top_picks || [];

  return (
    <div
      data-testid="match-dna-panel"
      style={{
        background: "var(--bc-panel, var(--bc-panel, #09090B))",
        border: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
        borderTop: "2px solid #22FFA3",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="match-dna-toggle"
        className="w-full px-4 py-3 flex items-center gap-3 cursor-pointer transition-all hover:bg-white/[0.02]"
        style={{ background: "transparent", border: 0 }}
      >
        <Dna size={16} style={{ color: "#22FFA3", filter: "drop-shadow(0 0 6px rgba(34,255,163,0.5))" }} />
        <span
          className="font-heading font-black text-xs uppercase tracking-[0.2em]"
          style={{ color: "#22FFA3" }}
        >
          MATCH DNA
        </span>
        <span className="font-data text-[10px] uppercase tracking-widest" style={{ color: "var(--bc-sub, #71717A)" }}>
          · {dna?.n_matches ? `${dna.n_matches} partite gemelle (sim ≥ 65%)` : "fino a 50 partite gemelle"} · verdetto empirico
        </span>
        {dna && conf && (
          <span
            className="ml-auto font-data text-[10px] font-bold uppercase tracking-wider px-2 py-1"
            style={{
              color: conf.color,
              border: `1px solid ${conf.color}66`,
              background: `${conf.color}10`,
              boxShadow: conf.glow,
            }}
          >
            CONFIDENCE {conf.label} · {Math.round(dna.strength_score || 0)}
          </span>
        )}
        {open ? <ChevronUp size={16} style={{ color: "var(--bc-sub, #71717A)", marginLeft: dna ? 8 : "auto" }} />
              : <ChevronDown size={16} style={{ color: "var(--bc-sub, #71717A)", marginLeft: dna ? 8 : "auto" }} />}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading && (
            <div className="py-6 text-center font-data text-[11px] uppercase tracking-wider" style={{ color: "var(--bc-sub, #71717A)" }}>
              <span className="inline-block animate-pulse">Sto cercando le 50 partite gemelle…</span>
            </div>
          )}
          {error && !loading && (
            <div className="py-4 text-center font-data text-xs" style={{ color: "#FF6FD8" }}>
              {error}
            </div>
          )}
          {!loading && !error && dna && (dna.n_matches || 0) === 0 && (
            <div className="py-4 text-center font-data text-xs" style={{ color: "var(--bc-sub, #71717A)" }}>
              {dna.message || "Nessuna partita storica simile disponibile."}
            </div>
          )}

          {!loading && !error && dna && (dna.n_matches || 0) > 0 && (
            <>
              {/* Tier + Sample size + Quality flag */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {tier && (
                  <span
                    data-testid="match-dna-tier"
                    className="font-data text-[10px] uppercase tracking-wider px-2 py-1"
                    style={{
                      color: tier.color,
                      border: `1px solid ${tier.color}55`,
                      background: `${tier.color}10`,
                    }}
                  >
                    {tier.label}
                  </span>
                )}
                {dna.quality?.quality_relaxed && (
                  <span
                    data-testid="match-dna-quality-warning"
                    className="font-data text-[10px] uppercase tracking-wider px-2 py-1"
                    style={{
                      color: "#F6C643",
                      border: "1px solid rgba(246,198,67,0.4)",
                      background: "rgba(246,198,67,0.08)",
                    }}
                    title="Pool gemelle sotto-soglia (<15 match con sim ≥ 65%) — verdetto da prendere con cautela"
                  >
                    ⚠ SAMPLE DILUITO
                  </span>
                )}
                <span className="font-data text-[10px] uppercase tracking-wider" style={{ color: "var(--bc-sub, #71717A)" }}>
                  · campione: <span style={{ color: "var(--bc-ink, #FFFFFF)" }}>{dna.n_matches}</span> match
                  {dna.effective_n != null && (
                    <> · peso effettivo <span style={{ color: "var(--bc-ink, #FFFFFF)" }}>{dna.effective_n}</span></>
                  )}
                  {" · media gol "}
                  <span style={{ color: "var(--bc-ink, #FFFFFF)" }}>{dna.media_gol_totali}</span>
                </span>
              </div>

              {/* Top 3 Picks — solo i 3 esiti più verificati storicamente */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {topPicks.slice(0, 3).map((p, i) => {
                  const accent = CATEGORY_ACCENT[p.category] || "#22FFA3";
                  const catLabel = CATEGORY_LABELS[p.category] || p.category;
                  const isOU = p.category === "OU" && p.over && p.under;
                  const isMGCombo = p.category === "MULTIGOL" && p.home_leg && p.away_leg;
                  return (
                    <div
                      key={p.key || i}
                      data-testid={`match-dna-pick-${i}`}
                      className="p-3 flex flex-col gap-1.5 transition-all"
                      style={{
                        background: `${accent}08`,
                        border: `1px solid ${accent}33`,
                        boxShadow: `0 0 18px ${accent}1a`,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className="font-data text-[10px] uppercase tracking-widest"
                          style={{ color: accent }}
                        >
                          {catLabel}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {p.divergence && (
                            <span
                              data-testid={`match-dna-divergence-${i}`}
                              className="font-data text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5"
                              style={{
                                color: "#F6C643",
                                border: "1px solid rgba(246,198,67,0.5)",
                                background: "rgba(246,198,67,0.12)",
                                lineHeight: 1.2,
                              }}
                              title={`Il modello predittivo dice ${p.model_pick || "altro"} (${p.model_pct || "?"}%) — DNA storico dice il contrario. Segnale di incertezza.`}
                            >
                              ⚠ DIVERGENZA
                            </span>
                          )}
                          <span
                            className="font-data text-[10px] uppercase tracking-wider"
                            style={{ color: "var(--bc-sub, #71717A)" }}
                          >
                            #{i + 1}
                          </span>
                        </div>
                      </div>
                      {isOU ? (
                        // Slot OU "doppio": Over (min 1.5) + Under (max 3.5)
                        <div className="flex flex-col gap-2 mt-0.5">
                          <div className="flex items-baseline justify-between">
                            <div className="flex flex-col">
                              <span className="font-data text-[10px] uppercase" style={{ color: "var(--bc-sub, #71717A)" }}>OVER</span>
                              <span className="font-heading font-black uppercase" style={{ color: "var(--bc-ink, #FFFFFF)", fontSize: "0.92rem" }}>
                                {p.over.label}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <span
                                className="font-data font-bold"
                                style={{ color: accent, fontSize: "1.15rem", textShadow: `0 0 10px ${accent}55` }}
                              >
                                {p.over.pct}%
                              </span>
                              <span className="font-data text-[10px]" style={{ color: "var(--bc-sub, #71717A)" }}>
                                {p.over.count}/{p.over.n}
                              </span>
                            </div>
                          </div>
                          <div className="h-px" style={{ background: `${accent}22` }} />
                          <div className="flex items-baseline justify-between">
                            <div className="flex flex-col">
                              <span className="font-data text-[10px] uppercase" style={{ color: "var(--bc-sub, #71717A)" }}>UNDER</span>
                              <span className="font-heading font-black uppercase" style={{ color: "var(--bc-ink, #FFFFFF)", fontSize: "0.92rem" }}>
                                {p.under.label}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <span
                                className="font-data font-bold"
                                style={{ color: accent, fontSize: "1.15rem", textShadow: `0 0 10px ${accent}55` }}
                              >
                                {p.under.pct}%
                              </span>
                              <span className="font-data text-[10px]" style={{ color: "var(--bc-sub, #71717A)" }}>
                                {p.under.count}/{p.under.n}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : isMGCombo ? (
                        // Slot MULTIGOL "combo": Casa + Trasferta sempre affiancati
                        // ``pct`` esposto è l'hit-rate dell'INTERSEZIONE (entrambe verificate)
                        <div className="flex flex-col gap-2 mt-0.5">
                          <div className="flex items-baseline justify-between">
                            <div className="flex flex-col">
                              <span className="font-data text-[10px] uppercase" style={{ color: "var(--bc-sub, #71717A)" }}>CASA</span>
                              <span className="font-heading font-black uppercase" style={{ color: "var(--bc-ink, #FFFFFF)", fontSize: "0.92rem" }}>
                                {p.home_leg.label}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <span
                                className="font-data font-bold"
                                style={{ color: accent, fontSize: "1.15rem", textShadow: `0 0 10px ${accent}55` }}
                              >
                                {p.home_leg.pct}%
                              </span>
                              <span className="font-data text-[10px]" style={{ color: "var(--bc-sub, #71717A)" }}>
                                {p.home_leg.count}/{p.home_leg.n}
                              </span>
                            </div>
                          </div>
                          <div className="h-px" style={{ background: `${accent}22` }} />
                          <div className="flex items-baseline justify-between">
                            <div className="flex flex-col">
                              <span className="font-data text-[10px] uppercase" style={{ color: "var(--bc-sub, #71717A)" }}>TRASFERTA</span>
                              <span className="font-heading font-black uppercase" style={{ color: "var(--bc-ink, #FFFFFF)", fontSize: "0.92rem" }}>
                                {p.away_leg.label}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <span
                                className="font-data font-bold"
                                style={{ color: accent, fontSize: "1.15rem", textShadow: `0 0 10px ${accent}55` }}
                              >
                                {p.away_leg.pct}%
                              </span>
                              <span className="font-data text-[10px]" style={{ color: "var(--bc-sub, #71717A)" }}>
                                {p.away_leg.count}/{p.away_leg.n}
                              </span>
                            </div>
                          </div>
                          <div className="mt-1 pt-1.5 flex items-baseline justify-between"
                            style={{ borderTop: `1px dashed ${accent}44` }}>
                            <span className="font-data text-[10px] uppercase tracking-wider" style={{ color: accent }}>
                              COMBO (ENTRAMBE)
                            </span>
                            <span
                              className="font-data font-bold"
                              style={{ color: accent, fontSize: "1.0rem", textShadow: `0 0 8px ${accent}77` }}
                            >
                              {p.pct}% <span className="font-data text-[10px]" style={{ color: "var(--bc-sub, #71717A)" }}>{p.count}/{p.n}</span>
                            </span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className="font-heading font-black uppercase tracking-tight"
                            style={{ color: "var(--bc-ink, #FFFFFF)", fontSize: "0.95rem", lineHeight: 1.1 }}
                          >
                            {p.label}
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span
                              className="font-data font-bold"
                              style={{ color: accent, fontSize: "1.4rem", lineHeight: 1, textShadow: `0 0 12px ${accent}66` }}
                            >
                              {p.pct}%
                            </span>
                            <span className="font-data text-[11px]" style={{ color: "var(--bc-sub, #71717A)" }}>
                              {p.count}/{p.n} match
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Heatmap Risultati Esatti — griglia 5x5 (0-4 per lato).
                  Layout viola con celle grandi, tutte le % sempre visibili.
                  La cella TOP 1 è bright violet, le altre scalano per intensità. */}
              {dna.exact_score_grid && dna.exact_score_grid_max_pct > 0 && (() => {
                const dim = dna.exact_score_grid_dim || 5;
                const maxPct = dna.exact_score_grid_max_pct || 1;
                const top1Score = (dna.exact_score_top3 || [])[0]?.score;
                const axis = Array.from({ length: dim }, (_, i) => String(i));
                return (
                  <div className="mt-3 p-4" style={{
                    background: "rgba(180,135,255,0.04)",
                    border: "1px solid rgba(180,135,255,0.18)",
                  }} data-testid="match-dna-heatmap">
                    <div className="flex items-center gap-2 mb-3">
                      <Dna size={13} style={{ color: "#B487FF" }} />
                      <span className="font-data text-[11px] uppercase tracking-widest font-bold" style={{ color: "#B487FF" }}>
                        HEATMAP {dim}×{dim}
                      </span>
                      <span className="font-data text-[10px] uppercase tracking-widest" style={{ color: "var(--bc-sub, #71717A)" }}>
                        — RISULTATI ESATTI
                      </span>
                      <span className="ml-auto font-data text-[10px] uppercase tracking-wider" style={{ color: "var(--bc-sub, #71717A)" }}>
                        {dna.n_matches} gemelle
                      </span>
                    </div>

                    {/* Column labels (gol trasferta) */}
                    <div className="grid mb-2" style={{ gridTemplateColumns: `24px repeat(${dim}, 1fr)`, gap: 6 }}>
                      <div></div>
                      {axis.map((lbl) => (
                        <div key={`x-${lbl}`} className="text-center font-data text-[12px]" style={{ color: "var(--bc-sub, #9CA3AF)", fontWeight: 500 }}>
                          {lbl}
                        </div>
                      ))}
                    </div>

                    {/* Rows */}
                    {axis.map((rowLbl) => (
                      <div key={`row-${rowLbl}`} className="grid mb-1.5" style={{ gridTemplateColumns: `24px repeat(${dim}, 1fr)`, gap: 6 }}>
                        <div className="flex items-center justify-end pr-1 font-data text-[12px]" style={{ color: "var(--bc-sub, #9CA3AF)", fontWeight: 500 }}>
                          {rowLbl}
                        </div>
                        {axis.map((colLbl) => {
                          const cellKey = `${rowLbl}-${colLbl}`;
                          const cell = dna.exact_score_grid[cellKey] || { pct: 0, count: 0 };
                          const intensity = maxPct > 0 ? Math.min(1, cell.pct / maxPct) : 0;
                          const isTop1 = cellKey === top1Score;
                          // Background viola: dark base (0.06) → bright (0.55) modulato da intensity
                          const bgAlpha = 0.06 + intensity * 0.50;
                          return (
                            <div
                              key={cellKey}
                              title={`${cellKey} · ${cell.pct}% (${cell.count}/${dna.n_matches})`}
                              style={{
                                height: 56,
                                background: isTop1
                                  ? "rgba(180,135,255,0.55)"
                                  : `rgba(180,135,255,${bgAlpha})`,
                                border: isTop1
                                  ? "1.5px solid rgba(220,180,255,1)"
                                  : "1px solid rgba(180,135,255,0.18)",
                                boxShadow: isTop1
                                  ? "0 0 20px rgba(180,135,255,0.7), inset 0 0 14px rgba(220,180,255,0.3)"
                                  : (intensity > 0.6 ? "0 0 8px rgba(180,135,255,0.25)" : "none"),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <span
                                className="font-data tabular-nums"
                                style={{
                                  color: isTop1 ? "#FFFFFF" : (intensity > 0.45 ? "#FFFFFF" : "rgba(255,255,255,0.55)"),
                                  fontSize: isTop1 ? "1rem" : "0.85rem",
                                  fontWeight: isTop1 ? 700 : (intensity > 0.45 ? 600 : 500),
                                  textShadow: isTop1 ? "0 0 6px rgba(255,255,255,0.4)" : "none",
                                  lineHeight: 1,
                                }}
                              >
                                {cell.pct > 0 ? `${cell.pct}%` : "—"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ))}

                    {/* Team labels: home a sinistra (asse Y), away in basso (asse X) */}
                    <div className="grid mt-2" style={{ gridTemplateColumns: `24px repeat(${dim}, 1fr)`, gap: 6 }}>
                      <div className="font-data text-[10px] uppercase tracking-wider" style={{ color: "var(--bc-sub, #71717A)", writingMode: "horizontal-tb" }}>
                        {(homeTeamName || "Casa").slice(0, 6)}
                      </div>
                      <div className="text-center font-data text-[10px] uppercase tracking-wider col-span-full" style={{ color: "var(--bc-sub, #71717A)", gridColumn: `2 / span ${dim}` }}>
                        {awayTeamName || "Trasferta"}
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="mt-3 flex items-center gap-2 font-data text-[10px] uppercase tracking-wider" style={{ color: "var(--bc-sub, #71717A)" }}>
                <ShieldCheck size={12} style={{ color: "#22FFA3" }} />
                Strength Score combina dominanza esito (60%) · similarity media (30%) · consenso modello (10%)
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MatchDNAPanel;
